/**
 * Display formatting helpers.
 */

import type { SubmitterGender } from '@/types';

/**
 * Full display name from first and last name.
 * Skips empty parts and trims. Use for in-app display (cards, lists, shell).
 */
export function formatFullName(
  firstName?: string | null,
  lastName?: string | null
): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  if (!first && !last) return '';
  if (!last) return first;
  return `${first} ${last}`;
}

/**
 * Short name for public display: first name + first initial of last name (e.g. "Jane S.").
 * Use on public-facing prayer cards and anywhere we don't want to expose the full last name
 * (same format as ProPresenter and RSS submitter feed).
 */
export function formatShortName(
  firstName?: string | null,
  lastName?: string | null
): string {
  const first = (firstName ?? '').trim();
  const last = (lastName ?? '').trim();
  if (!first && !last) return '';
  if (!last) return first;
  return `${first} ${last[0]}.`;
}

/** Human-readable label for submitter gender (moderator context only). */
export function formatGender(gender?: SubmitterGender | string | null): string {
  switch (gender) {
    case 'male':
      return 'Male';
    case 'female':
      return 'Female';
    case 'prefer_not_to_say':
      return 'Prefer not to say';
    default:
      return '';
  }
}

/** Location string safe for public display (hidden when anonymous). */
export function publicLocation(
  location: string | null | undefined,
  isAnonymous: boolean
): string {
  if (isAnonymous) return '';
  return (location ?? '').trim();
}

/** Truncate prayer body for recap previews. */
export function truncateBody(body: string, maxLength = 80): string {
  const trimmed = body.trim();
  if (trimmed.length <= maxLength) return trimmed;
  return `${trimmed.slice(0, maxLength).trimEnd()}…`;
}
